/**
 * Map a "Create drafts" form payload to one bulk-create item per leads list,
 * honouring per-drop sender/text overrides. Feeds runPreparedCreates so the
 * whole batch is created in ONE request instead of N sequential round-trips.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { PrepareDraftsPayload } from '@/features/smswiser-campaigns/components/PrepareNewDropsForm/shared/PrepareNewDropsForm.types';

import type { PreparedCreateItem } from '../hooks/runPreparedCreates';

export function buildCreateDraftItems(
  payload: PrepareDraftsPayload,
  ctx: { countryKey: string; wiserId: number; workDate: string },
): PreparedCreateItem[] {
  return payload.leadsListWiserIds.map((_id, i) => ({
    countryKey: ctx.countryKey,
    wiserId: ctx.wiserId,
    payload: buildDraftPayload(payload, ctx.workDate, i),
  }));
}

/** One prepared-drop payload for leads list at index `i` (same shape as the legacy per-item create). */
function buildDraftPayload(
  payload: PrepareDraftsPayload,
  workDate: string,
  i: number,
): Record<string, unknown> {
  return {
    workDate,
    routeId: payload.routeId,
    leadsListWiserId: payload.leadsListWiserIds[i]!,
    shortLinkTldValue: payload.shortLinkTldValue,
    amount: payload.amount,
    executeAfter: payload.executeAfter,
    senderNames: pickPerDrop(payload.senderNamesPerDrop, i, payload.senderNames),
    smsTexts: pickPerDrop(payload.smsTextsPerDrop, i, payload.smsTexts),
    rotators: payload.rotators ?? undefined,
    batchAmount: payload.sendInBatches ? payload.batchAmount : undefined,
    batchDelay: batchDelaySeconds(payload),
    forceSend: payload.forceSend,
    ...aiFields(payload),
    useOnlyNewDomains: payload.useOnlyNewDomains,
    clickIdStructure: payload.clickIdStructure,
    domainScheme: payload.domainScheme,
    staticTagId: payload.staticTagId ?? undefined,
  };
}

/** AI-optimization fields (passed through verbatim, same as the legacy create). */
function aiFields(payload: PrepareDraftsPayload): Record<string, unknown> {
  return {
    aiOptimizationEnabled: payload.aiOptimizationEnabled,
    aiOptimizationType: payload.aiOptimizationType,
    aiOptimizationBatchAmount: payload.aiOptimizationBatchAmount,
    aiOptimizationMinimalValuePercent: payload.aiOptimizationMinimalValuePercent,
    aiOptimizationSendUntil: payload.aiOptimizationSendUntil,
  };
}

/** Batch delay is stored in seconds; the form provides minutes when batching is on. */
function batchDelaySeconds(payload: PrepareDraftsPayload): number | undefined {
  if (!payload.sendInBatches || payload.batchDelay == null) return undefined;
  return payload.batchDelay * 60;
}

/** Per-drop override array wins when it has entries for index `i`, else the shared value. */
function pickPerDrop(
  perDrop: string[][] | undefined,
  i: number,
  fallback: string[],
): string[] {
  const override = perDrop?.[i];
  return override?.length ? override : fallback;
}
